import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormikContext, getIn } from "formik";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";

export default function FormikRemoteAutocompleteField({ name, label, loadOptions, getOptionLabel, onChange, sx, reloadDeps }) {
  const { values, setFieldValue } = useFormikContext();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(true);
  const perPage = 10;
  const lastSelectedRef = useRef(null);
  const prefetchedRef = useRef(false);
  const loadFnRef = useRef(loadOptions);
  const [open, setOpen] = useState(false);
  const value = getIn(values, name);

  // Keep latest loader without changing fetch callbacks
  useEffect(() => {
    loadFnRef.current = loadOptions;
  }, [loadOptions]);

  const fetchPage = useCallback(async (targetPage = 1) => {
    setLoading(true);
    try {
      const res = await Promise.resolve(loadFnRef.current?.({ page: targetPage, per_page: perPage }));
      const data = res?.data ?? res?.results ?? res ?? [];
      const next = res?.meta?.next_page ?? null;
      setHasNext(!(next === null || next === undefined));
      setOptions((prev) => (targetPage === 1 ? (Array.isArray(data) ? data : []) : [...prev, ...(Array.isArray(data) ? data : [])]));
      setPage(targetPage);
    } catch (e) {
      if (targetPage === 1) setOptions([]);
      setHasNext(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Pre-fetch first page when value is set but options haven't loaded (edit mode)
  useEffect(() => {
    if (value && options.length === 0 && !prefetchedRef.current) {
      prefetchedRef.current = true;
      fetchPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Load when dropdown opens (lazy)
  useEffect(() => {
    if (!open) return;
    if (options.length === 0) {
      setHasNext(true);
      setPage(1);
      fetchPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Controlled reload when explicit deps change (e.g., parent filter id)
  useEffect(() => {
    if (reloadDeps === undefined) return; // not controlled
    // If open, refetch immediately; otherwise clear so next open fetches
    setOptions([]);
    setHasNext(true);
    setPage(1);
    if (open) fetchPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, Array.isArray(reloadDeps) ? reloadDeps : [reloadDeps]);

  const selected = useMemo(() => {
    const match = options.find((o) => String(o?.id) === String(value)) || null;
    if (match) return match;
    if (lastSelectedRef.current && String(lastSelectedRef.current?.id) === String(value)) {
      return lastSelectedRef.current;
    }
    return null;
  }, [options, value]);

  // Ensure value is retained after options load
  useEffect(() => {
    if (value && options.length > 0 && !selected) {
      // Options loaded but selection not found, try to set it again
      const found = options.find((o) => String(o?.id) === String(value));
      if (found) {
        setFieldValue(name, found.id);
      }
    }
  }, [value, options, selected, name, setFieldValue]);

  // Cache last concrete selected option to avoid UI clearing during refetch
  useEffect(() => {
    if (selected) {
      lastSelectedRef.current = selected;
    }
  }, [selected]);

  return (
    <Autocomplete
      sx={sx}
      fullWidth
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      loading={loading}
      value={selected}
      getOptionLabel={(option) => getOptionLabel?.(option) || option?.name || option?.code || ""}
      isOptionEqualToValue={(option, v) => String(option?.id) === String(v?.id)}
      onChange={(_, v) => {
        setFieldValue(name, v?.id || "");
        onChange?.(v);
      }}
      ListboxProps={{
        onScroll: (e) => {
          const el = e.currentTarget;
          if (!loading && hasNext && el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
            fetchPage(page + 1);
          }
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          size="small"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
